// The fleet-load framing, in three pieces: what the numbers mean (the header),
// where they stand right now (the summary), and where they have been (the
// trend). They are one idea but not one element — the failure row and the reject
// panel sit between the summary and the trend in the layout — so they ship as
// three exports from one module rather than three modules or one wrapper that
// would have to fragment the page's vertical rhythm.
//
// None of them fetch, poll or hold the trend history. The view owns the ref the
// series is derived from and hands the derived array down.

import { Thermometer } from 'lucide-react';
import { Sparkline, Stat } from './healthPrimitives';
import { TOKEN_COST } from './tokenLoad';

export function HealthHeader({
  hasSnapshot,
  orgOptions,
  selectedOrg,
  setSelectedOrg,
}: {
  /** Gates the org picker: with no snapshot there is nothing to drill into. */
  hasSnapshot: boolean;
  orgOptions: { id: string; label: string }[];
  selectedOrg: string;
  setSelectedOrg: (id: string) => void;
}) {
  return (
  <header>
    <div className="flex items-center gap-2">
      <Thermometer size={20} className="text-orange-400" />
      <h1 className="text-foreground text-xl font-bold">G1 Health</h1>
      <span className="text-muted-foreground ml-2 text-xs">
        Per-account Kalshi load in <span className="text-foreground">tokens</span>, vs each account&apos;s
        tier limit
      </span>
      {hasSnapshot && (
        <select
          value={selectedOrg}
          onChange={(ev) => setSelectedOrg(ev.target.value)}
          className="border-border bg-muted text-foreground ml-auto rounded-md border px-2 py-1 text-xs"
          title="Drill into one org, or show the whole fleet"
        >
          <option value="all">All orgs ({orgOptions.length})</option>
          {orgOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </div>
    <p className="text-muted-foreground mt-2 text-xs leading-snug">
      Kalshi meters two per-account token buckets — read &amp; write, ~{TOKEN_COST} tokens/request.
      Limits/sec: <span className="text-foreground">Basic 200r/100w</span>,{' '}
      <span className="text-foreground">Advanced 300r/300w</span>. % = token rate ÷ that account&apos;s
      limit. Read/write buckets are independent — a 429 backoff sheds reads only, never order placement
      (writes). Reflects this build&apos;s relay (g1.pm = staging; gbuff = live trader load).
    </p>
  </header>
  );
}

export function FleetSummary({
  selectedOrg,
  accountCount,
  peakRead,
  peakWrite,
  throttledCount,
}: {
  selectedOrg: string;
  accountCount: number;
  peakRead: number;
  peakWrite: number;
  throttledCount: number;
}) {
  return (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
    <Stat label={selectedOrg === 'all' ? 'Active accounts' : 'Accounts in org'} value={String(accountCount)} />
    <Stat
      label="Peak read load"
      value={`${peakRead}%`}
      sub="of an account's read limit"
      tone={peakRead >= 100 ? 'danger' : peakRead >= 75 ? 'warn' : 'normal'}
    />
    <Stat
      label="Peak write load"
      value={`${peakWrite}%`}
      sub="of an account's write limit"
      tone={peakWrite >= 100 ? 'danger' : peakWrite >= 75 ? 'warn' : 'normal'}
    />
    <Stat
      label="In 429 backoff"
      value={String(throttledCount)}
      tone={throttledCount > 0 ? 'danger' : 'normal'}
    />
  </div>
  );
}

export function PeakLoadTrend({
  selectedOrg,
  series,
  curLoad,
  peakLoad,
  spanMinutes,
}: {
  selectedOrg: string;
  /** Peak %-of-limit per poll, oldest first, already scoped to the selection. */
  series: number[];
  curLoad: number;
  peakLoad: number;
  /** How far back the series reaches, in whole minutes — a function of the poll
   *  cadence and history depth, which belong to the view, not to this chart. */
  spanMinutes: number;
}) {
  return (
  <section className="border-border rounded-lg border p-4">
    <div className="mb-2 flex items-baseline justify-between">
      <h2 className="text-foreground text-sm font-semibold">
        Peak load — % of limit
        <span className="text-muted-foreground ml-2 text-[11px] font-normal">
          {selectedOrg === 'all' ? 'worst account across the fleet' : 'worst account in this org'} ·
          100% = at the bucket limit
        </span>
      </h2>
      <span className="text-muted-foreground font-mono text-xs">
        now {curLoad}% · peak {peakLoad}% · last ~{spanMinutes}m
      </span>
    </div>
    <Sparkline
      values={series}
      color={peakLoad >= 100 ? '#ef4444' : peakLoad >= 75 ? '#f59e0b' : '#3b82f6'}
      refValue={100}
    />
  </section>
  );
}
